/**
 * Registry of simulated API hits — the data behind the SIMULATED DATA banner.
 *
 * Standing rule from the owner: anything simulated has to be visibly labeled.
 * Every generator's payload carries `simulated: true` and a `demo_note`, and
 * every screen that consumed one shows the banner. This module is the seam
 * between the two: the demo API router calls `recordSimulatedHit()` for each
 * simulated endpoint it answers, and `<SimulatedDataBanner />` subscribes.
 *
 * Scope is "since the last route change". Hits are tagged with the pathname
 * that was current when they landed, and `resetOnNavigate(path)` drops every
 * hit belonging to a different path — deliberately NOT a blanket clear, because
 * a page's own queries can fire before the banner's navigation effect runs and
 * a blanket clear would swallow them.
 */
import { useSyncExternalStore } from 'react';

export interface SimulatedHit {
  /** The `/api/...` path that was answered with generated data. */
  endpoint: string;
  /** Human name of the integration it stands in for, e.g. "QuickBooks". */
  integration: string;
  /** Pathname of the screen that was open when the hit landed. */
  path: string;
}

/** Endpoint → integration label. First match wins, so order is significant. */
const INTEGRATIONS: { match: RegExp; name: string }[] = [
  { match: /^\/api\/qb-time/, name: 'QuickBooks Time' },
  { match: /^\/api\/qb[-/]/, name: 'QuickBooks' },
  { match: /^\/api\/gps\//, name: 'GPS Insight' },
  { match: /^\/api\/field-status/, name: 'GPS Insight' },
  { match: /^\/api\/m365\//, name: 'Microsoft 365' },
  { match: /^\/api\/(rc|sms|ringcentral)\//, name: 'RingCentral' },
  { match: /^\/api\/coaching\//, name: 'Operations coaching' },
  { match: /^\/api\/drive-time/, name: 'Google Routes' },
];

export function integrationFor(endpoint: string): string {
  return INTEGRATIONS.find((i) => i.match.test(endpoint))?.name ?? 'External integration';
}

const hits = new Map<string, SimulatedHit>();
const listeners = new Set<() => void>();

/** Cached snapshot — useSyncExternalStore needs a stable reference between changes. */
let snapshot: SimulatedHit[] = [];

function publish(): void {
  snapshot = [...hits.values()];
  for (const l of listeners) l();
}

function currentPath(): string {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

/**
 * Called by the demo API router every time it answers a request with generated
 * data. Idempotent per (endpoint, path).
 */
export function recordSimulatedHit(endpoint: string): void {
  const path = currentPath();
  const key = `${path}|${endpoint}`;
  if (hits.has(key)) return;
  hits.set(key, { endpoint, integration: integrationFor(endpoint), path });
  publish();
}

/**
 * Forget every hit that belongs to a different screen. Called by the banner
 * whenever the router's pathname changes, and by the `popstate` listener below
 * for back/forward navigation that happens outside the router.
 */
export function resetOnNavigate(path: string = currentPath()): void {
  let changed = false;
  for (const [key, hit] of hits) {
    if (hit.path !== path) {
      hits.delete(key);
      changed = true;
    }
  }
  if (changed) publish();
}

/** Test seam — drops everything. */
export function clearSimulatedHits(): void {
  if (!hits.size) return;
  hits.clear();
  publish();
}

export function subscribeSimulatedHits(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function getSimulatedHits(): SimulatedHit[] {
  return snapshot;
}

/** Subscribe a component to the hits recorded since the last route change. */
export function useSimulatedHits(): SimulatedHit[] {
  return useSyncExternalStore(subscribeSimulatedHits, getSimulatedHits, getSimulatedHits);
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => resetOnNavigate());
}
